import { useT } from "@transifex/react";
import moment from "moment";
import type { Moment } from "moment";
import { ShareTimings } from "../../../enums/shareFlow";
import type { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import DateTimeInput from "../../../basic/dateTimeInput/DateTimeInput";
import FormInfoRow from "../../../basic/FormInfoRow";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { isMomentInPast } from "../../../utils/dateUtils";
import ShareOptionCard from "../ShareOptionCard";

interface ShareTimingStepProps {
    questionText: string;
    nowDescription: string;
    selectedTiming: ShareTimings;
    onSelectTiming: (timing: ShareTimings) => void;
    visibilityDateValue: Moment | null;
    onVisibilityDateChange: (value: Moment | null) => void;
    deadlineValue?: Moment | null;
    classActivityCategory: ClassActivityCategory;
}

const ShareTimingStep = (props: ShareTimingStepProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const deadlineDate = props.deadlineValue && props.deadlineValue.isValid() ? props.deadlineValue : undefined;

    const t_nowTitle = t("Right away", { _tags: "ShareFlow, ShareTimingStep" });
    const t_laterTitle = t("Later", { _tags: "ShareFlow, ShareTimingStep" });
    const t_laterDescription = t("You choose date and time.", { _tags: "ShareFlow, ShareTimingStep" });
    const t_visibleImmediately = t("Will be visible immediately because the selected time is in the past.", {
        _tags: "ShareFlow, ShareTimingStep",
    });
    const t_visibilityInfoText = deadlineDate
        ? t("The deadline is on {date}, {time}. The visibility date cannot be after that.", {
              _tags: "ShareFlow, ShareTimingStep",
              date: deadlineDate.format("DD.MM.YYYY"),
              time: deadlineDate.format("HH:mm"),
          })
        : undefined;

    const timingCards = [
        {
            timing: ShareTimings.NOW,
            title: t_nowTitle,
            description: props.nowDescription,
        },
        {
            timing: ShareTimings.LATER,
            title: t_laterTitle,
            description: t_laterDescription,
        },
    ];

    const renderTimingCards = () =>
        timingCards.map((card) => (
            <ShareOptionCard
                key={card.timing}
                data-testid={`share-timing-option-${card.timing}`}
                isSelected={props.selectedTiming === card.timing}
                classActivityCategory={props.classActivityCategory}
                onClick={() => props.onSelectTiming(card.timing)}
                className="flex flex-col items-center justify-center gap-xs min-h-[104px]"
            >
                <p className="text-xl font-semibold text-p6-grey-dark">{card.title}</p>
                <p className="text-m text-p6-grey-dark">{card.description}</p>
            </ShareOptionCard>
        ));

    return (
        <div className="flex flex-col gap-m w-full">
            <p className="text-l text-p6-grey-dark">{props.questionText}</p>

            <div className={`grid gap-m ${getStyleBasedOnBreakpoint(Breakpoint.SM, "grid-cols-2", "grid-cols-1")}`}>
                {renderTimingCards()}
            </div>

            {props.selectedTiming === ShareTimings.LATER && (
                <div className="flex flex-col gap-xs">
                    <DateTimeInput
                        value={props.visibilityDateValue}
                        onChange={props.onVisibilityDateChange}
                        minDate={moment()}
                        maxDate={deadlineDate}
                        maxTime={deadlineDate}
                        infoText={t_visibilityInfoText}
                    />
                    {isMomentInPast(props.visibilityDateValue) && (
                        <FormInfoRow warningCategory={props.classActivityCategory}>{t_visibleImmediately}</FormInfoRow>
                    )}
                </div>
            )}
        </div>
    );
};

export default ShareTimingStep;
